export type StudioSaveState = 'saving' | 'saved' | 'failed';

/**
 * Saves studio projects one request at a time: projects handed over while a save is in
 * flight collapse into the latest, which is saved next. `flush` settles once nothing is
 * left to save.
 */
export function createStudioSaver<P>(
  persist: (project: P) => Promise<void>,
  onState: (state: StudioSaveState) => void,
): { save: (project: P) => void; flush: () => Promise<void> } {
  let next: { project: P } | null = null;
  let running: Promise<void> | null = null;

  const drain = async (): Promise<void> => {
    while (next) {
      const { project } = next;
      next = null;
      onState('saving');
      try {
        await persist(project);
        if (!next) onState('saved');
      } catch {
        onState('failed');
      }
    }
    running = null;
  };

  return {
    save(project) {
      next = { project };
      running ??= drain();
    },
    flush: () => running ?? Promise.resolve(),
  };
}
